import { useMemo, useState } from 'react';
import { PATH_SEPARATOR } from '@ic/security-registry';
import { DynamicIcon } from './DynamicIcon';
import { cn } from '@/lib/utils';
import type { MenuDesktop, MenuItem } from '@/types';

interface DesktopGroupProps {
  desktop: MenuDesktop;
  isExpanded: boolean;
  onToggle: () => void;
  onItemClick: (item: MenuItem) => void;
  filter?: string; // already lowercased + trimmed; empty string = no filter
  activeToolId?: string | null;
}

interface FolderNode {
  name: string;
  path: string;
  items: MenuItem[];
  folders: FolderNode[];
}

// D-105: 22px for sub-group headers; each deeper nested folder adds 22px.
function folderIndentFor(depth: number): string {
  return `${22 + depth * 22}px`;
}

function buildFolderTree(items: MenuItem[]): {
  rootItems: MenuItem[];
  rootFolders: FolderNode[];
} {
  const rootItems: MenuItem[] = [];
  const rootFolders: FolderNode[] = [];

  for (const item of items) {
    if (!item.folder) {
      rootItems.push(item);
      continue;
    }
    const segments = item.folder.split(PATH_SEPARATOR).filter(Boolean);
    let folders = rootFolders;
    let path = '';
    let leaf: FolderNode | null = null;
    for (const segment of segments) {
      path = path ? [path, segment].join(PATH_SEPARATOR) : segment;
      let node = folders.find((f) => f.name === segment);
      if (!node) {
        node = { name: segment, path, items: [], folders: [] };
        folders.push(node);
      }
      leaf = node;
      folders = node.folders;
    }
    leaf?.items.push(item);
  }

  return { rootItems, rootFolders };
}

function sortItems(items: MenuItem[]): void {
  items.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

// D-096: declared section order wins over alphabetical. `desktop.sections`'s key order in
// desktops.json IS the author's declared order (e.g. Reporting's Vault → Audit → Accounting →
// Management is deliberately not alphabetical) — honor it for the folders it names, falling back
// to alphabetical for anything undeclared. Only applies one level deep: `sections` maps top-level
// folder *names* to icons, so it has no ordering data for nested sub-folders.
function sortFolders(folders: FolderNode[], declaredOrder: readonly string[] = []): void {
  folders.sort((a, b) => {
    const aIdx = declaredOrder.indexOf(a.name);
    const bIdx = declaredOrder.indexOf(b.name);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.name.localeCompare(b.name);
  });
  for (const folder of folders) {
    sortItems(folder.items);
    sortFolders(folder.folders);
  }
}

interface FolderNodeViewProps {
  node: FolderNode;
  depth: number;
  expandedFolders: Set<string>;
  onToggleFolder: (path: string) => void;
  onItemClick: (item: MenuItem) => void;
  sections?: Record<string, string>;
  activeToolId?: string | null;
}

function FolderNodeView({
  node,
  depth,
  expandedFolders,
  onToggleFolder,
  onItemClick,
  sections,
  activeToolId,
}: Readonly<FolderNodeViewProps>) {
  const isExpanded = expandedFolders.has(node.path);
  const sectionIcon = sections?.[node.name] ?? 'folder';

  return (
    <div className="mb-0.5">
      {/* Sub-group header — matches .ic-sub-grp-hdr */}
      <button
        onClick={() => onToggleFolder(node.path)}
        style={{ paddingLeft: folderIndentFor(depth) }}
        className="flex w-full items-center gap-[calc(--spacing(1.75))] py-1.5 pr-2.5 text-left text-xs font-semibold text-white/50 hover:bg-white/5 hover:text-white/80 rounded transition-colors"
      >
        <DynamicIcon name={sectionIcon} className="h-[calc(--spacing(3.25))] w-[calc(--spacing(3.25))] flex-shrink-0 text-white/30" />
        <span className="flex-1 truncate">{node.name}</span>
        <DynamicIcon
          name="chevron-right"
          className={cn('h-[calc(--spacing(2.75))] w-[calc(--spacing(2.75))] flex-shrink-0 text-white/20 transition-transform duration-150', isExpanded && 'rotate-90')}
        />
      </button>

      {isExpanded && (
        <>
          {/* Items container — D-105: ~31px item indent. D-118: leaf items carry no icon. */}
          {node.items.length > 0 && (
            <div className="ml-[calc(--spacing(4.75))] mt-0.5 mb-0.5 border-l border-white/[0.07] pl-3">
              {node.items.map((item) => {
                const isActive = item.id === activeToolId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-label={item.label}
                    onClick={() => onItemClick(item)}
                    className={cn(
                      'flex w-full items-center py-1 pr-2.5 text-left text-[calc(--spacing(2.875))] border-l-2 overflow-hidden rounded-r-sm transition-all',
                      isActive
                        ? 'border-primary bg-primary/[0.16] text-nav-tool'
                        : 'border-transparent text-white/40 hover:bg-white/[0.04] hover:text-white/80',
                    )}
                  >
                    <span className="flex-1 truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
          {/* Nested sub-groups */}
          {node.folders.map((child) => (
            <FolderNodeView
              key={child.path}
              node={child}
              depth={depth + 1}
              expandedFolders={expandedFolders}
              onToggleFolder={onToggleFolder}
              onItemClick={onItemClick}
              sections={sections}
              activeToolId={activeToolId}
            />
          ))}
        </>
      )}
    </div>
  );
}

function collectAllFolderPaths(folders: FolderNode[], target: Set<string>): void {
  for (const folder of folders) {
    target.add(folder.path);
    collectAllFolderPaths(folder.folders, target);
  }
}

export function DesktopGroup({ desktop, isExpanded, onToggle, onItemClick, filter = '', activeToolId }: Readonly<DesktopGroupProps>) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const isFiltering = filter.length > 0;

  function toggleFolder(path: string) {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }

  const filteredItems = useMemo(() => {
    if (!isFiltering) return desktop.items;
    return desktop.items.filter((item) => item.label.toLowerCase().includes(filter));
  }, [desktop.items, isFiltering, filter]);

  const { rootItems, rootFolders } = useMemo(() => {
    const tree = buildFolderTree(filteredItems);
    sortItems(tree.rootItems);
    sortFolders(tree.rootFolders, Object.keys(desktop.sections ?? {}));
    return tree;
  }, [filteredItems, desktop.sections]);

  // While filtering, every folder containing matches is auto-expanded so users can
  // see the matched items immediately. The user's manual `expandedFolders` state is
  // preserved untouched, so closing the filter restores their previous tree state.
  const effectiveExpandedFolders = useMemo(() => {
    if (!isFiltering) return expandedFolders;
    const set = new Set<string>();
    collectAllFolderPaths(rootFolders, set);
    return set;
  }, [isFiltering, expandedFolders, rootFolders]);

  return (
    <div className="mb-0.5">
      {/* Desktop group header — D-105: 10px indent. */}
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-[calc(--spacing(1.75))] pl-[calc(--spacing(2.5))] pr-3.5 pt-[calc(--spacing(1.75))] pb-[calc(--spacing(1.25))] text-left text-[calc(--spacing(2.375))] font-bold uppercase tracking-[calc(--spacing(0.225))] text-sidebar-fg/60 hover:text-sidebar-fg/90 cursor-pointer select-none"
      >
        <DynamicIcon name={desktop.icon} className="h-[calc(--spacing(3.25))] w-[calc(--spacing(3.25))] flex-shrink-0 text-sidebar-fg/50" />
        <span className="flex-1 truncate">{desktop.label}</span>
        <DynamicIcon
          name="chevron-right"
          className={cn('h-3 w-3 flex-shrink-0 text-sidebar-fg/45 transition-transform duration-150', isExpanded && 'rotate-90')}
        />
      </button>

      {isExpanded && (
        <div className="pb-0.5">
          {/* Root-level items (not in any folder) — D-105: ~31px indent. D-118: no leaf icon. */}
          {rootItems.length > 0 && (
            <div className="ml-[calc(--spacing(4.75))] mt-0.5 mb-0.5 border-l border-white/[0.07] pl-3">
              {rootItems.map((item) => {
                const isActive = item.id === activeToolId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-label={item.label}
                    onClick={() => onItemClick(item)}
                    className={cn(
                      'flex w-full items-center py-1 pr-2.5 text-left text-[calc(--spacing(2.875))] border-l-2 overflow-hidden rounded-r-sm transition-all',
                      isActive
                        ? 'border-primary bg-primary/[0.16] text-nav-tool'
                        : 'border-transparent text-white/40 hover:bg-white/[0.04] hover:text-white/80',
                    )}
                  >
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
          {rootFolders.map((folder) => (
            <FolderNodeView
              key={folder.path}
              node={folder}
              depth={0}
              expandedFolders={effectiveExpandedFolders}
              onToggleFolder={toggleFolder}
              onItemClick={onItemClick}
              sections={desktop.sections}
              activeToolId={activeToolId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
